import React, { useState, useEffect } from "react";
import apiService from "../../services/api";
import { formatCurrency } from "../../utils/currencyUtils";

const BalanceCallout = ({ balance, required, loading, onRefresh }) => {
  const available = balance?.amount ?? null;
  const sufficient = available !== null && parseFloat(available) >= parseFloat(required);
  const bg = available === null ? "#f8fafc" : sufficient ? "#f0fdf4" : "#fef2f2";
  const border = available === null ? "#e2e8f0" : sufficient ? "#86efac" : "#fca5a5";
  const textColor = available === null ? "#374151" : sufficient ? "#166534" : "#991b1b";

  return (
    <div style={{ backgroundColor: bg, border: `1px solid ${border}`, borderRadius: "0.375rem", padding: "0.875rem 1rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
        <div>
          <p style={{ margin: 0, fontSize: "0.8rem", color: "#6b7280", fontWeight: "500" }}>Required Wise USD Balance</p>
          <p style={{ margin: "0.2rem 0 0", fontSize: "1.1rem", fontWeight: "700", color: "#111827" }}>
            {formatCurrency(required)} USD
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <p style={{ margin: 0, fontSize: "0.8rem", color: "#6b7280", fontWeight: "500" }}>Your Current Balance</p>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", justifyContent: "flex-end", marginTop: "0.2rem" }}>
            <p style={{ margin: 0, fontSize: "1.1rem", fontWeight: "700", color: textColor }}>
              {available !== null ? `${formatCurrency(available)} USD` : "—"}
            </p>
            <button
              onClick={onRefresh}
              disabled={loading}
              title="Refresh balance"
              style={{
                backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0",
                borderRadius: "0.25rem", cursor: loading ? "not-allowed" : "pointer",
                fontSize: "0.8rem", fontWeight: "600", color: "#374151",
                padding: "0.3rem 0.6rem", opacity: loading ? 0.5 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {loading ? "Refreshing…" : "↻ Refresh"}
            </button>
          </div>
        </div>
      </div>
      {available !== null && (
        <p style={{ margin: "0.5rem 0 0", fontSize: "0.8rem", color: textColor, fontWeight: "500" }}>
          {sufficient
            ? "✓ Your balance is sufficient. You're good to confirm."
            : `⚠ Insufficient balance. Top up at least ${formatCurrency(parseFloat(required) - parseFloat(available))} USD in Wise, then click ↻ to refresh.`}
        </p>
      )}
    </div>
  );
};

const ConfirmButton = ({ stage, count, balance, required, onConfirm, btnPrimary }) => {
  const available = balance?.amount ?? null;
  const sufficient = available !== null && parseFloat(available) >= parseFloat(required);
  const busy = stage === "confirming";
  const disabled = busy || !sufficient;
  return (
    <button style={btnPrimary(disabled)} onClick={onConfirm} disabled={disabled}>
      {busy ? "Initiating…" : `Confirm & Pay ${count} Tutor${count !== 1 ? "s" : ""}`}
    </button>
  );
};

const WisePaymentModal = ({ yearMonth, selectedTutorIds, onClose, onSuccess }) => {
  const [stage, setStage] = useState("loading"); // loading | preview | confirming | done
  const [preview, setPreview] = useState(null);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      apiService.previewWisePayments(yearMonth, Array.from(selectedTutorIds)),
      apiService.getWiseBalance(),
    ])
      .then(([previewData, balanceData]) => {
        setPreview(previewData);
        setBalance(balanceData);
        setStage("preview");
      })
      .catch((err) => {
        setError(err.message);
        setStage("error");
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshBalance = async () => {
    setBalanceLoading(true);
    try {
      const b = await apiService.getWiseBalance();
      setBalance(b);
    } catch (err) {
      // silently ignore refresh errors
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleConfirm = async () => {
    setStage("confirming");
    try {
      const items = preview.items.map((item) => ({
        canonicalTutorId: item.canonicalTutorId,
        quoteId: item.quoteId,
        wiseAccountId: item.wiseAccountId,
        sourceAmount: item.sourceAmount,
        targetAmount: item.targetAmount,
        targetCurrency: item.targetCurrency,
      }));
      const res = await apiService.initiateWisePayments(yearMonth, items);
      setResults(res);
      setStage("done");
    } catch (err) {
      setError(err.message);
      setStage("error");
    }
  };

  const overlay = {
    position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  };
  const modal = {
    background: "white", borderRadius: "0.5rem", padding: "1.5rem",
    width: "min(90vw, 860px)", maxHeight: "85vh", display: "flex",
    flexDirection: "column", gap: "1rem", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  };
  const th = {
    backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", padding: "0.6rem 0.75rem",
    fontSize: "0.7rem", fontWeight: "600", color: "#64748b",
    textTransform: "uppercase", letterSpacing: "0.05em",
  };
  const td = {
    border: "1px solid #e2e8f0", padding: "0.6rem 0.75rem",
    fontSize: "0.8rem", color: "#374151",
  };
  const tdRight = { ...td, textAlign: "right" };

  const btnPrimary = (disabled) => ({
    backgroundColor: disabled ? "#93c5fd" : "#1d4ed8", color: "white",
    border: "none", borderRadius: "0.375rem", padding: "0.5rem 1.25rem",
    fontSize: "0.875rem", fontWeight: "600",
    cursor: disabled ? "not-allowed" : "pointer",
  });
  const btnSecondary = {
    backgroundColor: "#f1f5f9", color: "#374151", border: "1px solid #e2e8f0",
    borderRadius: "0.375rem", padding: "0.5rem 1.25rem",
    fontSize: "0.875rem", fontWeight: "600", cursor: "pointer",
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: "700", color: "#001272" }}>
            Initiate Wise Payments
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.25rem", cursor: "pointer", color: "#64748b" }}>×</button>
        </div>

        {stage === "loading" && (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <div style={{ border: "3px solid #f3f3f3", borderTop: "3px solid #1d4ed8", borderRadius: "50%", width: 36, height: 36, animation: "spin 1s linear infinite", margin: "0 auto 1rem" }} />
            <p style={{ color: "#64748b", margin: 0 }}>Fetching quotes from Wise…</p>
          </div>
        )}

        {stage === "error" && (
          <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "0.375rem", padding: "1rem" }}>
            <p style={{ color: "#991b1b", margin: 0, fontWeight: "600" }}>Error</p>
            <p style={{ color: "#7f1d1d", margin: "0.25rem 0 0", fontSize: "0.875rem" }}>{error}</p>
          </div>
        )}

        {(stage === "preview" || stage === "confirming") && preview && (
          <>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                  <tr>
                    <th style={th}>Tutor</th>
                    <th style={{ ...th, textAlign: "right" }}>USD Amount</th>
                    <th style={{ ...th, textAlign: "right" }}>Fee (USD)</th>
                    <th style={{ ...th, textAlign: "right" }}>From Balance</th>
                    <th style={{ ...th, textAlign: "right" }}>Receives</th>
                    <th style={th}>Est. Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((item) => (
                    <tr key={item.canonicalTutorId}>
                      <td style={td}>{item.tutorName}</td>
                      <td style={tdRight}>{formatCurrency(item.sourceAmount)}</td>
                      <td style={{ ...tdRight, color: "#dc2626" }}>
                        {item.feeTotal != null ? formatCurrency(item.feeTotal) : "—"}
                      </td>
                      <td style={{ ...tdRight, fontWeight: "600" }}>
                        {formatCurrency(item.charged ?? item.sourceAmount)}
                      </td>
                      <td style={{ ...tdRight, color: "#059669" }}>
                        {item.targetAmount != null
                          ? `${parseFloat(item.targetAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${item.targetCurrency}`
                          : "—"}
                      </td>
                      <td style={{ ...td, color: "#6b7280", fontSize: "0.75rem" }}>
                        {item.formattedEstimatedDelivery || "—"}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: "#f8fafc", borderTop: "2px solid #e2e8f0", fontWeight: "700" }}>
                    <td style={td}>Total ({preview.items.length} tutors)</td>
                    <td style={tdRight}>{formatCurrency(preview.totalSourceAmount)}</td>
                    <td style={{ ...tdRight, color: "#dc2626" }}>{formatCurrency(preview.totalFees)}</td>
                    <td style={tdRight}>{formatCurrency(preview.totalSourceAmount)}</td>
                    <td style={tdRight}></td>
                    <td style={td}></td>
                  </tr>
                </tbody>
              </table>
            </div>
                <BalanceCallout
              balance={balance}
              required={preview.totalSourceAmount}
              loading={balanceLoading}
              onRefresh={refreshBalance}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
              <button style={btnSecondary} onClick={onClose} disabled={stage === "confirming"}>Cancel</button>
              <ConfirmButton
                stage={stage}
                count={preview.items.length}
                balance={balance}
                required={preview.totalSourceAmount}
                onConfirm={handleConfirm}
                btnPrimary={btnPrimary}
              />
            </div>
          </>
        )}

        {stage === "done" && results && (
          <>
            <div style={{ backgroundColor: results.failed === 0 ? "#f0fdf4" : "#fffbeb", border: `1px solid ${results.failed === 0 ? "#86efac" : "#fcd34d"}`, borderRadius: "0.375rem", padding: "0.75rem 1rem" }}>
              <p style={{ margin: 0, fontWeight: "600", color: results.failed === 0 ? "#166534" : "#92400e" }}>
                {results.succeeded} transfer{results.succeeded !== 1 ? "s" : ""} initiated
                {results.failed > 0 && `, ${results.failed} failed`}
              </p>
              <p style={{ margin: "0.25rem 0 0", fontSize: "0.8rem", color: "#6b7280" }}>
                Tutors will be marked as paid once Wise confirms the funds are sent (typically within minutes to a day).
              </p>
            </div>
            {results.failed > 0 && (
              <div style={{ overflowY: "auto", maxHeight: "200px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
                  <thead>
                    <tr>
                      <th style={th}>Tutor ID</th>
                      <th style={th}>Transfer ID</th>
                      <th style={th}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.results.map((r) => (
                      <tr key={r.canonicalTutorId} style={{ backgroundColor: r.error ? "#fef2f2" : "white" }}>
                        <td style={td}>{r.canonicalTutorId}</td>
                        <td style={td}>{r.transferId || "—"}</td>
                        <td style={{ ...td, color: "#991b1b" }}>{r.error || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button style={btnPrimary(false)} onClick={() => { onSuccess(); onClose(); }}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WisePaymentModal;
